import { VCPM } from '../models/project.model';
import { PcsSizingEngineResponse as Engine } from '../models/pcs-sizing-engine-response.model';
import { buildClusterDisplayName, stripClusterSiteSuffix, buildConnectedWorkloadSummaries, buildWorkloadVmCountLookup } from './stretched-cluster.transform';
import { resolveClusterMembers } from '../adapters/sizing-recalculation.adapter';

/** Runs on the migration's clone. Repairs labels/usage only; never recalculates resources. */
export function normalizeProjectClusterOutputInPlace(project: VCPM.Project): void {
  const domains = project.data.workloadDomains ?? [];
  const workloadVmCounts = buildWorkloadVmCountLookup(domains);
  const output = project.data.output;
  const recommendations = output?.recommendation?.workloadDomainsRecommendation?.result?.recommendations ?? [];
  const renames = new Map<string, Set<string>>();
  const rawClusters: Engine.Cluster[] = [];

  for (const recommendation of recommendations) {
    const domain = domains.find(d => String(d.id) === String(recommendation.requirementsId));
    if (!domain) continue;
    for (const owner of recommendation.workloadRecommendations ?? []) {
      const workload = domain.workloadList.find(w => String(w.id) === String(owner.workloadId));
      if (!workload) continue;
      const seen = new Set<Engine.WorkloadRecommendation>();
      const legacyLabels = new Map<string, Engine.Cluster[]>();
      const visit = (variant: Engine.WorkloadRecommendation): void => {
        if (seen.has(variant)) return;
        seen.add(variant);
        const legacyNames = new Set((variant.clusters ?? []).filter(c => !c.clusterType || c.clusterType === 'HCI')
          .map(c => stripClusterSiteSuffix(c.clusterName ?? '')).filter(n => /^Cluster[- ]?\d+$/i.test(n)));
        for (const cluster of variant.clusters ?? []) {
          rawClusters.push(cluster);
          const legacyLabel = buildClusterDisplayName(cluster);
          if (legacyLabel) legacyLabels.set(legacyLabel, [...(legacyLabels.get(legacyLabel) ?? []), cluster]);
          const type = cluster.clusterType ?? 'HCI';
          const ref = type === 'SON' ? workload.storageCluster : workload.computeCluster;
          const entity = type === 'SON' ? project.data.clusterConfigurations?.find(c => c.id === ref?.id)
            : domain.clusterConfigurations?.find(c => c.id === ref?.id);
          const oldName = stripClusterSiteSuffix(cluster.clusterName ?? '');
          if (entity && type === 'HCI' && entity.type === 'HCI' && (!oldName || /^Cluster[- ]?\d+$/i.test(oldName))) {
            if (oldName) {
              const key = `${domain.id}:${oldName}`;
              const names = renames.get(key) ?? new Set<string>();
              names.add(entity.name);
              renames.set(key, names);
            }
            // Multiple legacy cluster names represent real split parts, not site duplicates.
            if (!cluster.clusterSplitName && legacyNames.size > 1) cluster.clusterSplitName = oldName;
            cluster.clusterName = entity.name;
          }
          if (!cluster.connectedWorkloads?.length) {
            const members = entity ? resolveClusterMembers(domains, entity.id, type) : [];
            cluster.connectedWorkloads = members.length ? members.map(member => {
              const memberDomain = domains.find(d => String(d.id) === member.requirementsId)!;
              const memberWorkload = memberDomain.workloadList.find(w => String(w.id) === member.workloadId)!;
              return { id: memberWorkload.id!, name: memberWorkload.workload?.workloadName || member.workloadId,
                workloadDomainId: memberDomain.id!, workloadDomainName: memberDomain.domainName };
            }) : [{ id: owner.workloadId, name: owner.name || workload.workload?.workloadName || owner.workloadId,
              workloadDomainId: domain.id!, workloadDomainName: domain.domainName }];
          }
        }
        variant.profileRecommendations?.forEach(visit);
        if (variant.selectedReadyNodeRecommendation?.result) visit(variant.selectedReadyNodeRecommendation.result);
      };
      visit(owner);
      const referencedEntities = [
        domain.clusterConfigurations?.find(c => c.id === workload.computeCluster?.id),
        project.data.clusterConfigurations?.find(c => c.id === workload.storageCluster?.id),
      ];
      referencedEntities.forEach(entity => { if (entity?.lastRecalculationResult?.result) visit(entity.lastRecalculationResult.result); });
      // Persisted summary workloads have names rather than workload ids: only use a unique match.
      const summaryDomain = output?.summary?.workloadDomains?.find(d => String(d.id) === String(domain.id));
      const summaryOwners = summaryDomain?.workloads.filter(w => w.name === owner.name) ?? [];
      if (summaryOwners.length === 1 && recommendation.workloadRecommendations.filter(w => w.name === owner.name).length === 1) {
        const variants = [...seen].flatMap(v => v.clusters ?? []);
        for (const summary of summaryOwners[0].clusters ?? []) {
          let candidates = variants.filter(c => summary.clusterId && c.clusterId === summary.clusterId);
          if (!candidates.length) {
            const named = legacyLabels.get(stripClusterSiteSuffix(summary.name)) ?? [];
            const names = new Set(named.map(buildClusterDisplayName));
            if (names.size === 1) candidates = named;
          }
          if (!candidates.length) {
            // A legacy standalone HCI workload belongs to one canonical entity, even with split/site output.
            const ref = workload.computeCluster;
            if (ref && workload.vcfDeploymentInputs?.deploymentType !== 'dHCI' &&
              (!summary.clusterType || summary.clusterType === 'HCI')) {
              const old = stripClusterSiteSuffix(summary.clusterBaseName || summary.name);
              const entity = domain.clusterConfigurations?.find(c => c.id === ref.id && c.type === 'HCI');
              if (entity && /^Cluster[- ]?\d+(?:\s|$)/i.test(old)) {
                summary.name = summary.clusterBaseName && summary.name.startsWith(summary.clusterBaseName)
                  ? [entity.name, stripClusterSiteSuffix(summary.name.slice(summary.clusterBaseName.length))].filter(Boolean).join(' ')
                  : entity.name;
                summary.clusterBaseName = entity.name;
              }
            }
            candidates = variants.filter(c => c.clusterName === summary.clusterBaseName &&
              buildClusterDisplayName(c) === stripClusterSiteSuffix(summary.name));
          }
          const cluster = candidates[0];
          if (cluster) {
            summary.name = buildClusterDisplayName(cluster);
            summary.clusterBaseName = cluster.clusterName;
            summary.connectedWorkloads = buildConnectedWorkloadSummaries(cluster.connectedWorkloads,
              { currentDomainId: String(domain.id), workloadVmCounts });
            summary.connectedWorkloadNames = [...new Set(cluster.connectedWorkloads?.map(w =>
              String(w.workloadDomainId) === String(domain.id) ? w.name : `${w.name} / ${w.workloadDomainName}`) ?? [])];
          } else if (!summary.connectedWorkloadNames?.length) {
            summary.connectedWorkloadNames = [owner.name];
          }
        }
      }
    }
  }
  // Cross-cluster labels are renamed only if their old name identifies one entity in that domain.
  for (const cluster of rawClusters) {
    cluster.connectedClusters?.forEach(connection => {
      const names = renames.get(`${connection.workloadDomainId}:${stripClusterSiteSuffix(connection.clusterName)}`);
      if (names?.size === 1) connection.clusterName = [...names][0];
    });
  }

  // Some older saves contain only a summary. Match its owner by a unique input workload name.
  for (const summaryDomain of output?.summary?.workloadDomains ?? []) {
    const domain = domains.find(d => String(d.id) === String(summaryDomain.id));
    if (!domain) continue;
    for (const owner of summaryDomain.workloads) {
      for (const summary of owner.clusters ?? []) {
        // Summary-only saves have no raw references. Recognize an old domain prefix using
        // the project's known foreign domains rather than splitting arbitrary entity names.
        const reverseLegacyLabel = (label: string): string => {
          const candidates = domains.filter(d => String(d.id) !== String(domain.id) &&
            label.startsWith(`${d.domainName} / `));
          if (candidates.length !== 1) return label;
          const prefix = `${candidates[0].domainName} / `;
          return `${label.slice(prefix.length)} / ${candidates[0].domainName}`;
        };
        for (const field of ['connectedWorkloadNames', 'mountedStorageClusterNames', 'connectedComputeClusterNames'] as const) {
          if (summary[field]) summary[field] = summary[field]!.map(reverseLegacyLabel);
        }
      }
      const inputs = domain.workloadList.filter(w => w.workload?.workloadName === owner.name);
      if (inputs.length !== 1) continue;
      const workload = inputs[0];
      for (const summary of owner.clusters ?? []) {
        const type = summary.clusterType ?? 'HCI';
        const ref = type === 'SON' ? workload.storageCluster : workload.computeCluster;
        const entity = type === 'SON' ? project.data.clusterConfigurations?.find(c => c.id === ref?.id)
          : domain.clusterConfigurations?.find(c => c.id === ref?.id);
        if (!summary.connectedWorkloadNames?.length) {
          const members = entity ? resolveClusterMembers(domains, entity.id, type) : [];
          summary.connectedWorkloadNames = members.length ? members.map(member => {
            const memberDomain = domains.find(d => String(d.id) === member.requirementsId)!;
            const memberWorkload = memberDomain.workloadList.find(w => String(w.id) === member.workloadId)!;
            const name = memberWorkload.workload?.workloadName || member.workloadId;
            return String(memberDomain.id) === String(domain.id) ? name : `${name} / ${memberDomain.domainName}`;
          }) : [owner.name!];
        }
        const oldBase = stripClusterSiteSuffix(summary.clusterBaseName || summary.name);
        if (entity?.type === 'HCI' && type === 'HCI' && /^Cluster[- ]?\d+$/i.test(oldBase)) {
          const tail = summary.clusterBaseName && summary.name.startsWith(summary.clusterBaseName)
            ? stripClusterSiteSuffix(summary.name.slice(summary.clusterBaseName.length)) : '';
          summary.name = [entity.name, tail].filter(Boolean).join(' ');
          summary.clusterBaseName = entity.name;
        }
      }
    }
  }
  for (const summaryDomain of output?.summary?.workloadDomains ?? []) {
    const domain = domains.find(d => String(d.id) === String(summaryDomain.id));
    const cards = [...(summaryDomain.ownedStorageClusters ?? []), ...summaryDomain.workloads.flatMap(w => w.clusters ?? [])];
    for (const card of cards) {
      if (!card.connectedWorkloads?.length && (card.clusterType === 'SON' || card.clusterRole === 'STORAGE')) {
        let entityId = card.canonicalStorageClusterId;
        if (!entityId) {
          // Only a unique input owner can recover a legacy summary's canonical storage reference.
          const owner = summaryDomain.workloads.find(w => w.clusters?.includes(card));
          const owners = domain?.workloadList.filter(w => w.workload?.workloadName === owner?.name) ?? [];
          if (owners.length === 1) entityId = owners[0].storageCluster?.id;
        }
        const members = entityId ? resolveClusterMembers(domains, entityId, 'SON') : [];
        const connections = members.flatMap(member => {
          const inputDomain = domains.find(d => String(d.id) === member.requirementsId);
          const input = inputDomain?.workloadList.find(w => String(w.id) === member.workloadId);
          return input && inputDomain ? [{ id: input.id!, name: input.workload?.workloadName || member.workloadId,
            workloadDomainId: inputDomain.id!, workloadDomainName: inputDomain.domainName }] : [];
        });
        card.connectedWorkloads = buildConnectedWorkloadSummaries(connections,
          { currentDomainId: String(summaryDomain.id), workloadVmCounts });
      }
      // Refresh persisted counts from current inputs, including zero and missing inputs.
      card.connectedWorkloads?.forEach(w => {
        w.vmCount = workloadVmCounts.get(JSON.stringify([String(w.domainId), String(w.workloadId)]));
      });
    }
  }
}
